import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FadeUp, MaskLine } from "./Reveal";
import LogoMark from "./LogoMark";

// PROJECT MEDIA -------------------------------------------------------------
// Assets live in /app/frontend/public/media/projects/hot-ones-game-night/.
// The case study page is at /work/hot-ones-game-night.
const PROJECT = {
    heroMedia: {
        type: "video",
        src: "/media/projects/hot-ones-game-night/intro.mp4",
        poster: "/media/projects/hot-ones-game-night/intro-poster.jpg",
    },
    supportMedia: [
        { label: "Title card", type: "image", fit: "cover", src: "/media/projects/hot-ones-game-night/title-card.webp" },
        { label: "Key art", type: "image", fit: "contain", src: "/media/projects/hot-ones-game-night/keyart.webp" },
    ],
};

function MediaFrame({ media, label, testid }) {
    return (
        <figure
            data-testid={testid}
            className="relative aspect-video overflow-hidden border border-black/15 bg-black"
        >
            {media.type === "video" ? (
                <video
                    className="h-full w-full object-cover"
                    src={media.src}
                    poster={media.poster}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                />
            ) : media.type === "image" ? (
                <img
                    className={`h-full w-full ${media.fit === "contain" ? "object-contain" : "object-cover"}`}
                    src={media.src}
                    alt={label}
                    loading="lazy"
                />
            ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-center">
                    <LogoMark className="h-8 w-8" bg={null} fg="#E31B23" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/60">
                        Asset pending delivery
                    </span>
                </div>
            )}
            <span className="absolute right-3 top-3 border border-white/30 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-white/60">
                {label}
            </span>
        </figure>
    );
}

export default function LatestProject() {
    const navigate = useNavigate();
    const openCaseStudy = () => navigate("/work/hot-ones-game-night");

    return (
        <section
            id="latest-project"
            data-testid="latest-project-section"
            className="bg-white px-5 pb-20 pt-24 text-black md:px-8 md:pb-28 md:pt-36"
        >
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] md:text-xs">
                <FadeUp>
                    <span className="text-mmm-red">001</span> — Latest Project
                </FadeUp>
                <FadeUp className="hidden text-black/50 md:block">The money shot</FadeUp>
            </div>

            <h2
                data-testid="latest-project-title"
                className="mt-10 font-display text-[clamp(2.8rem,8.5vw,8.5rem)] uppercase leading-[0.88] md:mt-14"
            >
                <MaskLine delay={0.05}>First we feast:</MaskLine>
                <MaskLine delay={0.15}>
                    <span className="text-mmm-red">Hot Ones Game Night</span>
                </MaskLine>
            </h2>

            <FadeUp delay={0.1} className="mt-10 md:mt-14">
                <p
                    data-testid="latest-project-lead"
                    className="max-w-3xl font-display text-2xl uppercase leading-tight md:text-4xl"
                >
                    A game show needs more than graphics. It needs a world.
                </p>
            </FadeUp>

            <div id="project-media" data-testid="project-media" className="mt-12 scroll-mt-24 md:mt-16">
                <FadeUp>
                    <div
                        data-testid="project-media-hero"
                        onClick={openCaseStudy}
                        className="cursor-pointer"
                    >
                        <MediaFrame media={PROJECT.heroMedia} label="Project hero media" />
                    </div>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-black/45 md:text-xs">
                        Visual universe — logo / game environments / pieces / motion design
                    </p>
                </FadeUp>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    {PROJECT.supportMedia.map((m, i) => (
                        <FadeUp key={i} delay={0.08 + i * 0.06}>
                            <MediaFrame media={m} label={m.label} testid={`project-media-support-${i + 1}`} />
                        </FadeUp>
                    ))}
                </div>
            </div>

            <div className="mt-14 grid grid-cols-12 gap-6 md:mt-20">
                <FadeUp
                    delay={0.1}
                    className="col-span-12 text-base leading-relaxed text-black/60 md:col-span-8 md:text-lg"
                >
                    <p data-testid="latest-project-description">
                        <strong className="text-black">
                            A new way to play with fire.
                        </strong>{" "}
                        Money Mac Media developed the visual identity for Hot Ones:
                        Game Night, bringing the climb to Mount Scoville to life
                        across branding, game design, broadcast, and motion.
                    </p>
                </FadeUp>
                <FadeUp delay={0.2} className="col-span-12 md:col-span-4 md:text-right">
                    <button
                        type="button"
                        data-testid="latest-project-cta"
                        onClick={openCaseStudy}
                        className="inline-block border-2 border-black bg-transparent px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-black hover:text-white"
                    >
                        See the work →
                    </button>
                </FadeUp>
            </div>
        </section>
    );
}
